import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import Menu from "./Menu";

import useDidMountEffect from "../utils/useDidMountEffect";

const Courses = (props) => {
  const { clients, categories } = props;
  const [courses, setCourses] = useState(props.courses);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);

  // Dropdown states
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");

  // Refs for dropdown management
  const categoryDropdownRef = useRef(null);
  const clientDropdownRef = useRef(null);

  const fetchCourses = (search) => {
    let url = "/courses?";
    if (search) {
      url += `search=${search}`;
    }
    if (selectedCategories.length > 0) {
      selectedCategories.forEach((category) => {
        url += `&category_ids[]=${category}`;
      });
    }
    if (selectedClients.length > 0) {
      selectedClients.forEach((client) => {
        url += `&client_ids[]=${client}`;
      });
    }

    fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setCourses(data.courses);
      });
  };

  useDidMountEffect(() => {
    fetchCourses(debouncedSearch);
  }, [debouncedSearch, selectedCategories, selectedClients]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search, 500]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setCategoryDropdownOpen(false);
      }
      if (
        clientDropdownRef.current &&
        !clientDropdownRef.current.contains(event.target)
      ) {
        setClientDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCategoryChange = (categoryId) => {
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(
        selectedCategories.filter((id) => id !== categoryId),
      );
      return;
    }
    setSelectedCategories([...selectedCategories, categoryId]);
  };

  const handleClientChange = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter((id) => id !== clientId));
      return;
    }
    setSelectedClients([...selectedClients, clientId]);
  };

  const coursesCards = courses.map((course) => {
    return (
      <div
        key={course.id}
        className="w-full border flex flex-col border-gray-200 overflow-hidden relative rounded-lg shadow-sm hover:shadow-md transition-shadow"
      >
        <Menu courseId={course.id} courses={courses} setCourses={setCourses} />
        <a href={`/courses/${course.id}/edit`}>
          {!course.logo_url ? (
            <div className="mb-4 w-full h-48 flex items-center justify-center border-gray-100 border-2 text-gray-500 bg-gray-50">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-8"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                />
              </svg>
            </div>
          ) : (
            <div className="mb-4 w-full h-48 border-gray-100 border-2 overflow-hidden bg-gray-50">
              <img
                className="w-full h-full object-contain"
                src={course.logo_url}
                alt={`${course.title} logo`}
              />
            </div>
          )}
        </a>

        <div className="p-4">
          <h3 className="font-semibold text-lg mb-1">{course.title}</h3>
          <p className="text-sm text-gray-700 mb-2">{course.description}</p>
          <div className="flex gap-2 text-xs mb-2">
            {course.categories.map((category) => (
              <span
                key={category.id}
                className="border-2 border-gray-100 px-2 py-0.5 text-gray-500"
              >
                {category.name}
              </span>
            ))}
          </div>
          <div className="text-right text-xs text-gray-500">
            {course.created_at}
          </div>
        </div>
      </div>
    );
  });

  const CategoryDropdown = () => {
    const filteredCategories = categories.filter((category) =>
      category.name.toLowerCase().includes(categorySearch.toLowerCase()),
    );

    const selectedCategoryNames = categories
      .filter((cat) => selectedCategories.includes(cat.id))
      .map((cat) => cat.name)
      .join(", ");

    return (
      <div className="mb-4" ref={categoryDropdownRef}>
        <h4 className="font-semibold mb-2">Kategoria</h4>
        <div className="relative">
          <button
            onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
            className="w-full p-2 text-left border border-gray-300 bg-white flex justify-between items-center text-sm"
          >
            <span className="truncate">
              {selectedCategoryNames || "Wybierz kategorie..."}
            </span>
            <svg
              className={`w-4 h-4 transition-transform ${categoryDropdownOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {categoryDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-60 overflow-y-auto">
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Szukaj kategorii..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className="w-full p-1 text-sm border border-gray-300"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="max-h-40 overflow-y-auto">
                {filteredCategories.map((category) => (
                  <div
                    key={category.id}
                    className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleCategoryChange(category.id)}
                  >
                    <input
                      type="checkbox"
                      className="accent-black"
                      checked={selectedCategories.includes(category.id)}
                      onChange={() => {}} // Handled by parent onClick
                    />
                    <label className="text-sm cursor-pointer flex-1">
                      {category.name}
                    </label>
                  </div>
                ))}
                {filteredCategories.length === 0 && (
                  <div className="p-2 text-sm text-gray-500">Brak wyników</div>
                )}
              </div>
              {selectedCategories.length > 0 && (
                <div className="p-2 border-t border-gray-200">
                  <button
                    onClick={() => setSelectedCategories([])}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Wyczyść wszystkie
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const ClientDropdown = () => {
    const filteredClients = clients.filter((client) =>
      client.name.toLowerCase().includes(clientSearch.toLowerCase()),
    );

    const selectedClientNames = clients
      .filter((client) => selectedClients.includes(client.id))
      .map((client) => client.name)
      .join(", ");

    return (
      <div className="mb-4" ref={clientDropdownRef}>
        <h4 className="font-semibold mb-2">Klient</h4>
        <div className="relative">
          <button
            onClick={() => setClientDropdownOpen(!clientDropdownOpen)}
            className="w-full p-2 text-left border border-gray-300 bg-white flex justify-between items-center text-sm"
          >
            <span className="truncate">
              {selectedClientNames || "Wybierz klientów..."}
            </span>
            <svg
              className={`w-4 h-4 transition-transform ${clientDropdownOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {clientDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-60 overflow-y-auto">
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Szukaj klientów..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="w-full p-1 text-sm border border-gray-300"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="max-h-40 overflow-y-auto">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleClientChange(client.id)}
                  >
                    <input
                      type="checkbox"
                      className="accent-black"
                      checked={selectedClients.includes(client.id)}
                      onChange={() => {}} // Handled by parent onClick
                    />
                    <label className="text-sm cursor-pointer flex-1">
                      {client.name}
                    </label>
                  </div>
                ))}
                {filteredClients.length === 0 && (
                  <div className="p-2 text-sm text-gray-500">Brak wyników</div>
                )}
              </div>
              {selectedClients.length > 0 && (
                <div className="p-2 border-t border-gray-200">
                  <button
                    onClick={() => setSelectedClients([])}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Wyczyść wszystkie
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-h-[85vh] flex h-screen">
      <div className="flex-1 p-2 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 auto-rows-max">
          {coursesCards}
        </div>
      </div>

      <aside className="w-full  max-w-xs p-4 border-l border-gray-200">
        <a
          className="flex items-center gap-2 bg-black text-white px-4 py-2 text-sm font-medium"
          href="/courses/new"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M12 4v16m8-8H4" />
          </svg>
          Nowe Szkolenie
        </a>
        <hr className="h-px my-4 bg-gray-200 border-0 dark:bg-gray-700" />
        <div className="relative">
          <input
            type="text"
            placeholder="Wyszukaj szkolenie..."
            className="pl-10 w-full p-2 border border-gray-300"
            onChange={(e) => setSearch(e.target.value)}
          />

          <svg
            className="w-4 h-4 absolute left-2.5 top-3.5 text-gray-400"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
          >
            <path d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1010.5 18a7.5 7.5 0 006.15-3.35z" />
          </svg>
        </div>
        <hr className="h-px my-4 bg-gray-200 border-0 dark:bg-gray-700" />
        <CategoryDropdown />
        <hr className="h-px my-4 bg-gray-200 border-0 dark:bg-gray-700" />
        <ClientDropdown />
      </aside>
    </div>
  );
};

Courses.propTypes = {
  clients: PropTypes.array,
  courses: PropTypes.array,
  categories: PropTypes.array,
};

export default Courses;
